/* =========================================================
   featured-product-carousel.js (Nebulyn – PRODUCTION STABLE)
   - Tabs switch collection groups
   - Indicator follows active tab (offset-based)
   - Carousel prev/next + dots + swipe + optional autoplay
   - Fetches universe JSON (view=neb-master-json) per collection
   - Calls NebCards.initGroup({ root, products, currency }) on active group
   - No scrollIntoView(), no vertical scroll/focus jumps
   - Multi-section + Shopify theme editor support
========================================================= */

(function () {
  'use strict';

  // ---------------------------
  // Utils
  // ---------------------------
  function warn() { try { console.warn.apply(console, arguments); } catch (e) {} }
  function normalize(v) { return (v || '').toString().trim(); }
  function clamp(n, min, max) { return Math.max(min, Math.min(n, max)); }

  function isProbablyHtml(txt) {
    var t = (txt || '').trim();
    if (!t) return true;
    var c = t[0];
    return !(c === '{' || c === '[');
  }

  function extractProducts(payload) {
    if (Array.isArray(payload)) return payload;
    if (payload && Array.isArray(payload.products)) return payload.products;
    return [];
  }

  function extractPages(payload) {
    var pages = payload && payload.meta && payload.meta.pages;
    pages = Number(pages || 1);
    return isFinite(pages) && pages > 0 ? pages : 1;
  }

  async function fetchJsonPage(collectionHandle, page) {
    var url =
      '/collections/' +
      encodeURIComponent(collectionHandle) +
      '?view=neb-master-json&page=' +
      String(page || 1);

    var resp = await fetch(url, { credentials: 'same-origin' });
    if (!resp.ok) return null;

    var txt = (await resp.text()).trim();
    if (!txt || isProbablyHtml(txt)) return null;

    try { return JSON.parse(txt); } catch (e) { return null; }
  }

  async function fetchUniverseForCollection(collectionHandle) {
    if (!collectionHandle) return [];

    var key = 'neb_universe_' + collectionHandle;

    try {
      var cached = sessionStorage.getItem(key);
      if (cached) return JSON.parse(cached) || [];
    } catch (e) {}

    var first = await fetchJsonPage(collectionHandle, 1);
    if (!first) return [];

    var products = extractProducts(first).slice();
    var pages = extractPages(first);

    if (pages > 1) {
      var toFetch = [];
      for (var p = 2; p <= pages; p++) toFetch.push(p);

      var CONCURRENCY = 3;
      var idx = 0;

      async function worker() {
        while (idx < toFetch.length) {
          var page = toFetch[idx++];
          var payload = await fetchJsonPage(collectionHandle, page);
          if (payload) products = products.concat(extractProducts(payload));
        }
      }

      var workers = [];
      for (var w = 0; w < Math.min(CONCURRENCY, toFetch.length); w++) workers.push(worker());
      await Promise.all(workers);
    }

    try { sessionStorage.setItem(key, JSON.stringify(products)); } catch (e) {}
    return products;
  }

  function waitForNebCards() {
    return new Promise(function (resolve) {
      if (window.NebCards && typeof window.NebCards.initGroup === 'function') return resolve(true);

      var tries = 0;
      var maxTries = 120; // ~6s at 50ms
      var t = setInterval(function () {
        tries++;
        if (window.NebCards && typeof window.NebCards.initGroup === 'function') {
          clearInterval(t);
          resolve(true);
        } else if (tries >= maxTries) {
          clearInterval(t);
          resolve(false);
        }
      }, 50);
    });
  }

  // ---------------------------
  // Tabs UI (active class + indicator)
  // ---------------------------
  function positionIndicator(tabsWrap, indicator, activeTab) {
    if (!tabsWrap || !indicator || !activeTab) return;
    // Offset based (no getBoundingClientRect loops)
    indicator.style.width = activeTab.offsetWidth + 'px';
    indicator.style.transform = 'translateX(' + activeTab.offsetLeft + 'px)';
  }

  function setActiveTab(tabsWrap, tabs, indicator, activeBtn) {
    if (!tabsWrap || !tabs || !tabs.length || !activeBtn) return;

    tabs.forEach(function (t) {
      var on = t === activeBtn;
      t.classList.toggle('is-active', on);
      t.setAttribute('aria-selected', on ? 'true' : 'false');
    });

    positionIndicator(tabsWrap, indicator, activeBtn);
  }

  // ---------------------------
  // Carousel engine (per section instance)
  // ---------------------------
  function createCarouselInstance(sectionEl) {
    var inst = {
      currentIndex: 0,
      timer: null,
      bound: false,
      groupEl: null,
      track: null,
      slides: []
    };

    function getBoolAttr(name, fallback) {
      var v = sectionEl.getAttribute(name);
      if (v === null || v === '') return !!fallback;
      return v === 'true' || v === '1';
    }
    function getIntAttr(name, fallback) {
      var n = parseInt(sectionEl.getAttribute(name), 10);
      return isFinite(n) ? n : fallback;
    }

    function perView() {
      var slidesDesktop = getIntAttr('data-slides-desktop', 4);
      var slidesMobile = getIntAttr('data-slides-mobile', 2);
      return window.innerWidth <= 749 ? slidesMobile : slidesDesktop;
    }

    function maxIndex() {
      return Math.max(0, (inst.slides || []).length - perView());
    }

    function stop() {
      if (inst.timer) {
        clearInterval(inst.timer);
        inst.timer = null;
      }
    }

    function updateDots() {
      var dotsWrap = sectionEl.querySelector('.neb-featured-carousel-dots');
      if (!dotsWrap) return;
      var dots = dotsWrap.querySelectorAll('.neb-featured-carousel-dot');
      dots.forEach(function (dot, i) {
        dot.classList.toggle('is-active', i === inst.currentIndex);
      });
    }

    function updateNav() {
      var prevBtn = sectionEl.querySelector('.neb-featured-carousel-nav--prev');
      var nextBtn = sectionEl.querySelector('.neb-featured-carousel-nav--next');

      var slides = inst.slides || [];
      var m = maxIndex();

      if (prevBtn) prevBtn.disabled = slides.length <= 1 || inst.currentIndex <= 0;
      if (nextBtn) nextBtn.disabled = slides.length <= 1 || inst.currentIndex >= m;

      updateDots();
    }

    function goTo(i) {
      var track = inst.track;
      var slides = inst.slides || [];

      if (!track || !slides.length) {
        if (track) track.style.transform = 'translateX(0px)';
        inst.currentIndex = 0;
        updateNav();
        return;
      }

      var m = maxIndex();
      inst.currentIndex = clamp(i, 0, m);

      var w = slides[0].offsetWidth || 0;
      track.style.transform = 'translateX(' + (-inst.currentIndex * w) + 'px)';
      updateNav();
    }

    function buildDots() {
      var dotsWrap = sectionEl.querySelector('.neb-featured-carousel-dots');
      if (!dotsWrap) return;

      dotsWrap.innerHTML = '';

      var slides = inst.slides || [];
      if (slides.length <= 1) return;

      var count = maxIndex() + 1;
      for (var i = 0; i < count; i++) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className =
          'neb-featured-carousel-dot' + (i === inst.currentIndex ? ' is-active' : '');
        b.setAttribute('data-index', String(i));
        b.setAttribute('aria-label', 'Go to slide ' + (i + 1));
        dotsWrap.appendChild(b);
      }
    }

    function start() {
      var autoplay = getBoolAttr('data-autoplay', true);
      var autoplaySpeedS = getIntAttr('data-autoplay-speed', 6);

      var slides = inst.slides || [];
      if (!autoplay || slides.length <= perView()) return;

      stop();
      inst.timer = setInterval(function () {
        var m = maxIndex();
        goTo(inst.currentIndex >= m ? 0 : inst.currentIndex + 1);
      }, autoplaySpeedS * 1000);
    }

    function bindOnce() {
      if (inst.bound) return;
      inst.bound = true;

      var prevBtn = sectionEl.querySelector('.neb-featured-carousel-nav--prev');
      var nextBtn = sectionEl.querySelector('.neb-featured-carousel-nav--next');
      var dotsWrap = sectionEl.querySelector('.neb-featured-carousel-dots');
      var viewport = sectionEl.querySelector('.neb-featured-carousel-viewport');

      if (prevBtn) {
        prevBtn.addEventListener('click', function () {
          stop();
          goTo(inst.currentIndex - 1);
          start();
        });
      }

      if (nextBtn) {
        nextBtn.addEventListener('click', function () {
          stop();
          goTo(inst.currentIndex + 1);
          start();
        });
      }

      if (dotsWrap) {
        dotsWrap.addEventListener('click', function (evt) {
          var dot = evt.target.closest('.neb-featured-carousel-dot');
          if (!dot) return;
          var i = parseInt(dot.getAttribute('data-index'), 10);
          if (!isFinite(i)) return;
          stop();
          goTo(i);
          start();
        });
      }

      // swipe
      if (viewport) {
        var startX = 0, startY = 0, tracking = false;

        viewport.addEventListener('touchstart', function (e) {
          if (!e.touches || e.touches.length !== 1) return;
          tracking = true;
          startX = e.touches[0].clientX;
          startY = e.touches[0].clientY;
        }, { passive: true });

        viewport.addEventListener('touchmove', function (e) {
          if (!tracking || !e.touches || e.touches.length !== 1) return;
          var dx = e.touches[0].clientX - startX;
          var dy = e.touches[0].clientY - startY;
          // prevent horizontal swipe from also scrolling page
          if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 10) e.preventDefault();
        }, { passive: false });

        viewport.addEventListener('touchend', function (e) {
          if (!tracking) return;
          tracking = false;

          var endX =
            e.changedTouches && e.changedTouches[0]
              ? e.changedTouches[0].clientX
              : startX;
          var dx = endX - startX;
          if (Math.abs(dx) < 40) return;

          stop();
          if (dx < 0) goTo(inst.currentIndex + 1);
          else goTo(inst.currentIndex - 1);
          start();
        }, { passive: true });
      }

      window.addEventListener('resize', function () {
        goTo(inst.currentIndex);
      });
    }

    function mountGroup(groupEl) {
      inst.groupEl = groupEl;
      inst.track = groupEl ? groupEl.querySelector('.neb-featured-carousel-track') : null;
      inst.slides = groupEl
        ? Array.prototype.slice.call(groupEl.querySelectorAll('.neb-featured-carousel-slide'))
        : [];

      bindOnce();
      stop();
      inst.currentIndex = 0;
      buildDots();

      requestAnimationFrame(function () {
        goTo(0);
        start();
      });
    }

    function destroy() {
      stop();
      inst.groupEl = null;
      inst.track = null;
      inst.slides = [];
    }

    return {
      mountGroup: mountGroup,
      destroy: destroy
    };
  }

  // ---------------------------
  // Cards init (per group)
  // ---------------------------
  async function initCardsForGroup(groupEl, collectionHandle, currency) {
    if (!groupEl || !collectionHandle) return;

    // Keep this caching — it prevents duplicate binding and fixes “Choose options”
    if (groupEl.dataset.nebCardsInit === 'true') return;

    var ok = await waitForNebCards();
    if (!ok) {
      warn('[FeaturedCarousel] NebCards not ready (neb-cards.js missing?)');
      return;
    }

    var products = await fetchUniverseForCollection(collectionHandle);
    if (!products || !products.length) {
      warn('[FeaturedCarousel] universe empty for', collectionHandle, '(view=neb-master-json?)');
      return;
    }

    window.NebCards.initGroup({
      root: groupEl,
      products: products,
      currency: currency || 'INR'
    });

    groupEl.dataset.nebCardsInit = 'true';
  }

  // ---------------------------
  // Section controller
  // ---------------------------
  function initSection(sectionEl) {
    if (!sectionEl || sectionEl.dataset.nebFeaturedBound === 'true') return;
    sectionEl.dataset.nebFeaturedBound = 'true';

    var currency = sectionEl.getAttribute('data-currency') || 'INR';

    var tabsWrap = sectionEl.querySelector('.neb-featured-tabs');
    var tabs = tabsWrap ? Array.prototype.slice.call(tabsWrap.querySelectorAll('.neb-featured-tab')) : [];
    var indicator = tabsWrap ? tabsWrap.querySelector('.neb-featured-tab-indicator') : null;

    var groups = Array.prototype.slice.call(sectionEl.querySelectorAll('.neb-collection-group'));

    var carousel = createCarouselInstance(sectionEl);
    sectionEl.__nebFeaturedCarousel = carousel;

    function updateExploreCTA(handle, title) {
      var link = sectionEl.querySelector('.neb-explore-collection-link');
      if (!link || !handle) return;

      link.href = '/collections/' + handle;

      var base = link.getAttribute('data-base-text') || 'SHOP';
      // Your liquid already prints "Shop <title>" initially.
      link.textContent = title ? (base + ' ' + title) : base;
      link.setAttribute('aria-label', link.textContent);
    }

    async function setActiveCollection(handle, clickedTabEl) {
      handle = normalize(handle);
      if (!handle) return;

      // Toggle groups
      var activeGroup = null;
      groups.forEach(function (g) {
        var h = normalize(g.getAttribute('data-collection-handle'));
        var isActive = h === handle;
        g.classList.toggle('is-active', isActive);
        g.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        if (isActive) activeGroup = g;
      });

      // Fallback to first group if handle mismatch
      if (!activeGroup && groups.length) {
        activeGroup = groups[0];
        handle = normalize(activeGroup.getAttribute('data-collection-handle'));
      }
      if (!activeGroup) return;

      // Tabs state + indicator
      if (tabsWrap && tabs.length) {
        var btnToActivate = clickedTabEl;
        if (!btnToActivate) {
          btnToActivate = tabs.find(function (t) {
            return normalize(t.getAttribute('data-collection-handle')) === handle;
          }) || tabs[0];
        }
        if (btnToActivate) setActiveTab(tabsWrap, tabs, indicator, btnToActivate);
      }

      // CTA
      var title = activeGroup.getAttribute('data-collection-title') || '';
      updateExploreCTA(handle, title);

      // Carousel mount
      carousel.mountGroup(activeGroup);

      // Cards init (this is what makes Choose Options work)
      await initCardsForGroup(activeGroup, handle, currency);
    }

    // Tab clicks (delegated)
    if (tabsWrap) {
      tabsWrap.addEventListener('click', function (evt) {
        var btn = evt.target.closest('.neb-featured-tab');
        if (!btn) return;

        var handle = btn.getAttribute('data-collection-handle');
        if (!handle) return;

        setActiveCollection(handle, btn);
      });
    }

    // Init: pick first active tab, else first tab, else first group
    var firstTab =
      (tabs && tabs.find(function (t) { return t.classList.contains('is-active'); })) ||
      (tabs && tabs[0]) ||
      null;

    var firstHandle = firstTab
      ? firstTab.getAttribute('data-collection-handle')
      : (groups[0] ? groups[0].getAttribute('data-collection-handle') : '');

    if (firstTab && tabsWrap) {
      setActiveTab(tabsWrap, tabs, indicator, firstTab);
    }

    setActiveCollection(firstHandle, firstTab);
  }

  // ---------------------------
  // Boot
  // ---------------------------
  function boot(root) {
    var ctx = root || document;
    var sections = ctx.querySelectorAll('[data-neb-featured-carousel]');
    sections.forEach(initSection);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { boot(document); });
  else boot(document);

  // Theme editor support
  document.addEventListener('shopify:section:load', function (evt) {
    var sid = evt && evt.detail && evt.detail.sectionId;
    if (!sid) return;

    var sec = document.querySelector('#neb-featured-carousel-' + sid);
    if (!sec) return;

    // reset flags so initSection runs again
    sec.dataset.nebFeaturedBound = '';

    var groups = sec.querySelectorAll('.neb-collection-group');
    groups.forEach(function (g) {
      g.dataset.nebCardsInit = '';
    });

    if (sec.__nebFeaturedCarousel && sec.__nebFeaturedCarousel.destroy) {
      sec.__nebFeaturedCarousel.destroy();
    }
    sec.__nebFeaturedCarousel = null;

    boot(sec);
  });
})();